import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Crown, Users, Store, TrendingUp, CheckCircle2, XCircle, RefreshCw } from "lucide-react";

const fmtTL = (v) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(Number(v) || 0);
const fmtNum = (v) => new Intl.NumberFormat("tr-TR").format(Number(v) || 0);

export default function SuperAdmin() {
  const [overview, setOverview] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [o, t] = await Promise.all([
        api.get("/superadmin/overview"),
        api.get("/superadmin/tenants"),
      ]);
      setOverview(o.data);
      setTenants(t.data.tenants || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yükleme hatası");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const suspend = async (tid, name) => {
    if (!window.confirm(`${name} adlı mağazanın erişimini durdur?`)) return;
    try {
      await api.post(`/superadmin/tenants/${tid}/suspend`);
      toast.success("Mağaza askıya alındı");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const activate = async (tid, name) => {
    const days = Number(window.prompt(`${name} için kaç günlük abonelik vermek istiyorsun?`, "30"));
    if (!days || days <= 0) return;
    try {
      await api.post(`/superadmin/tenants/${tid}/activate?days=${days}`);
      toast.success(`${days} gün abonelik verildi`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const filtered = tenants.filter((t) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (t.shop_name || "").toLowerCase().includes(s) || (t.owner_email || "").toLowerCase().includes(s);
  });

  return (
    <div className="space-y-8" data-testid="superadmin-page">
      <div>
        <div className="label-caps flex items-center gap-2"><Crown size={12} style={{color:"var(--gold)"}} /> Platform Sahibi</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2 font-light">Süper Admin Paneli</h1>
        <p className="text-neutral-400 mt-3 text-sm max-w-2xl">
          Sisteme kayıtlı tüm dükkanları buradan yönetirsin. Askıya al, aboneliğe erişim ver,
          gelir metriklerini takip et.
        </p>
      </div>

      {overview && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="superadmin-overview">
          <Stat icon={Store}   label="Toplam Mağaza"        value={fmtNum(overview.total_tenants)} testid="stat-tenants" />
          <Stat icon={Users}   label="Toplam Kullanıcı"     value={fmtNum(overview.total_users)}   testid="stat-users" />
          <Stat icon={CheckCircle2} label="Aktif Abonelik"  value={fmtNum(overview.active_subscriptions)} accent testid="stat-subs" />
          <Stat icon={TrendingUp}   label="Aylık Tahmini Gelir" value={fmtTL(overview.mrr_tl)} accent testid="stat-mrr" />
          <Stat icon={RefreshCw}    label="Toplam İşlem"    value={fmtNum(overview.total_transactions)} testid="stat-txn" />
        </div>
      )}

      <div className="flex items-center gap-3">
        <input
          type="text" value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Dükkan adı veya e-posta ile ara"
          className="flex-1 max-w-md bg-transparent border border-white/10 px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold]"
          data-testid="superadmin-search"
        />
        <button onClick={load} className="border border-white/10 px-4 py-3 text-sm hover:bg-white/5" data-testid="superadmin-refresh">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="border border-white/10 overflow-hidden" data-testid="superadmin-tenants-table">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a] text-[10px] tracking-widest uppercase text-neutral-500">
            <tr>
              <th className="text-left px-4 py-3">Dükkan</th>
              <th className="text-left px-4 py-3">Sahip</th>
              <th className="text-right px-4 py-3">Kullanıcı</th>
              <th className="text-right px-4 py-3">Ürün</th>
              <th className="text-right px-4 py-3">Satış</th>
              <th className="text-right px-4 py-3">Ciro</th>
              <th className="text-center px-4 py-3">Abonelik</th>
              <th className="text-right px-4 py-3">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => {
              const s = t._stats || {};
              const status = s.subscription_status;
              return (
                <tr key={t.id} className="border-t border-white/6 hover:bg-white/[.02]" data-testid={`superadmin-row-${t.id}`}>
                  <td className="px-4 py-3">
                    <div className="text-white">{t.shop_name}</div>
                    <div className="text-[10px] text-neutral-500 font-mono">{t.id.slice(0,8)}</div>
                  </td>
                  <td className="px-4 py-3 text-neutral-300">{t.owner_email}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums">{s.user_count ?? 0}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums">{s.product_count ?? 0}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums">{s.txn_count ?? 0}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums" style={{color:"var(--gold)"}}>{fmtTL(s.sales_total_tl)}</td>
                  <td className="px-4 py-3 text-center">
                    {status === "active" ? <span className="inline-flex items-center gap-1 text-emerald-400 text-xs"><CheckCircle2 size={12}/> Aktif</span>
                     : status === "pending" ? <span className="text-yellow-400 text-xs">Beklemede</span>
                     : status === "canceled" ? <span className="inline-flex items-center gap-1 text-red-400 text-xs"><XCircle size={12}/> İptal</span>
                     : <span className="text-neutral-500 text-xs">Deneme</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <button onClick={()=>activate(t.id, t.shop_name)} className="text-xs px-3 py-1 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid={`activate-${t.id}`}>
                        Süre Ver
                      </button>
                      <button onClick={()=>suspend(t.id, t.shop_name)} className="text-xs px-3 py-1 border border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid={`suspend-${t.id}`}>
                        Askıya Al
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && !loading && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-neutral-500 text-sm">Sonuç bulunamadı</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="card-flat p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-neutral-500">
        <Icon size={14} />
        <div className="label-caps">{label}</div>
      </div>
      <div className={`font-display text-2xl mt-2 ${accent ? "" : ""}`} style={accent ? {color:"var(--gold)"} : {}}>{value}</div>
    </div>
  );
}
